import { Fragment } from "react";
import { Menu, Transition } from "@headlessui/react";
import {
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  BuildingLibraryIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@gradebook/ui";
import { APP_NAME } from "@gradebook/shared";
import { cn } from "@/lib/utils";
import { ROUTES, useSchoolRoutes } from "@/lib/routes";
import { useAuthStore } from "@/store/authStore";
import { useHasUnreadNotes } from "@/hooks/useWhatsNew";
import { AcademicYearMenu } from "./AcademicYearMenu";
import { CourseScopePill, TermScopePill } from "./CourseScopeMenu";
import { HomeroomScopePill } from "./HomeroomScopeMenu";

interface AppTopbarProps {
  /** Opens the mobile nav drawer; the rail is hidden below `lg`. */
  onOpenNav: () => void;
}

/**
 * The shell's topbar: brand on the left, the class in scope beside it, the
 * global academic-year and term controls on the right, and the account menu at
 * the end.
 *
 * It is transparent by design — it sits directly on the tinted canvas rather
 * than being a bar of its own, which is what makes the year pill read as a
 * control over the whole app instead of one more item in a strip of chrome.
 */
export function AppTopbar({ onOpenNav }: AppTopbarProps) {
  const user = useAuthStore((state) => state.user);
  const memberships = useAuthStore((state) => state.memberships);
  const effectiveRole = useAuthStore((state) => state.getEffectiveRole());
  const isImpersonating = useAuthStore(
    (state) => state.impersonation?.isImpersonating ?? false
  );
  const isSupportSession = useAuthStore((state) => state.support !== null);
  const logout = useAuthStore((state) => state.logout);
  const routes = useSchoolRoutes();
  const navigate = useNavigate();
  const hasUnread = useHasUnreadNotes();

  // `memberships` is persisted, so a session that predates the multi-school
  // work rehydrates without the key at all; the brand must not blow up the
  // whole shell over a missing school name.
  const schoolName =
    (memberships ?? []).find((membership) => membership.schoolId === user?.schoolId)
      ?.schoolName ?? null;

  const handleLogout = () => {
    logout();
    navigate("/auth/login");
  };

  // The avatar shows initials, which name nobody. Who is signed in used to be
  // spelled out in the sidebar; now it lives behind this menu, so the button
  // itself has to say it.
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const accountLabel = [fullName ? `Account: ${fullName}` : "Account", hasUnread && "new updates"]
    .filter(Boolean)
    .join(", ");

  const canCreateSchool = user?.roles?.includes("admin") && !isImpersonating;

  return (
    <header
      data-testid="app-topbar"
      // `relative z-40` is what keeps the menus that hang off this bar above
      // the page: the panel below is a later sibling, so its own stacking
      // contexts — a gradebook's sticky table header at `z-30`, say — otherwise
      // paint straight over an open popover. Below the mobile nav drawer
      // (`z-50`), which has to cover the topbar in turn.
      className="relative z-40 flex h-[54px] flex-none items-center gap-3 px-1"
    >
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="rounded-[10px] text-neutral-500 hover:bg-card hover:text-neutral-700 lg:hidden"
        onClick={onOpenNav}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="size-5" />
      </Button>

      {/* `min-w-0` is what lets the school name actually truncate: a flex item
          defaults to `min-width: auto`, so without it the brand refuses to
          shrink below its text and pushes the whole shell wider than a phone. */}
      <div className="flex min-w-0 items-center gap-[9px] lg:w-[178px]">
        <div className="flex size-[26px] shrink-0 items-center justify-center rounded-[7px] bg-primary text-primary-foreground shadow-sm shadow-primary-500/30">
          <svg
            className="size-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
        </div>
        {/* The school name yields before the course does. On a phone the topbar
            has room for the mark, one scope pill and the account — and of the
            two names, the class being graded is the one the page is about. The
            school is still named in the account menu. */}
        <span className="hidden truncate text-[13px] font-semibold text-neutral-900 sm:inline">
          {schoolName ?? APP_NAME}
        </span>
      </div>

      {/* The class in scope sits next to the brand rather than out on the
          right: it is the thing being worked on, where year and term are the
          frame around it. Only present on pages that have a course scope. */}
      {/* At most one of these is ever up: each renders only while a page with
          that scope is on screen, and no page has both. */}
      <div className="flex min-w-0 flex-1 items-center">
        <CourseScopePill />
        <HomeroomScopePill />
      </div>

      <AcademicYearMenu />
      <TermScopePill />

      <Menu as="div" className="relative flex-none">
        <Menu.Button
          aria-label={accountLabel}
          className="relative flex size-7 items-center justify-center rounded-full bg-neutral-300 text-neutral-700 transition-colors hover:bg-neutral-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="text-[10px] font-bold uppercase leading-none">
            {initials(user?.firstName, user?.lastName)}
          </span>
          {hasUnread && (
            <span
              data-testid="whats-new-dot"
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full bg-primary-600 ring-2 ring-background"
            />
          )}
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-150"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 top-10 z-30 w-64 overflow-hidden rounded-[16px] border bg-card shadow-[0_16px_40px_rgba(0,0,0,0.16)] focus:outline-none">
            <div className="flex items-center gap-3 border-b border-neutral-100 px-3.5 py-3">
              <UserCircleIcon className="size-9 shrink-0 text-neutral-400" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-neutral-900">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                <p className="text-xs capitalize text-neutral-400">
                  {user?.roles?.join(', ')}
                  {isImpersonating && (
                    <span className="ml-1 text-warning-600">(as {effectiveRole})</span>
                  )}
                </p>
              </div>
            </div>
            <div className="p-1">
              {/* Another school on this same login. Offered to admins only:
                  anyone can create one from the public signup form, so this
                  restricts nothing - it just keeps an invitation to start a
                  school out of a student's account menu. Hidden while
                  impersonating, where the person on screen is not the person
                  the school would belong to (the API refuses it too). */}
              {canCreateSchool && (
                <Menu.Item>
                  {({ focus }) => (
                    <Link
                      to={ROUTES.newSchool()}
                      className={cn(
                        focus ? "bg-muted" : "",
                        "flex h-auto w-full items-center justify-start gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal text-neutral-700"
                      )}
                    >
                      <BuildingLibraryIcon className="size-4" />
                      Create another school
                    </Link>
                  )}
                </Menu.Item>
              )}
              {/* Everyone, unlike the admin-only item above it. Changing your
                  own password is not an administrative act, and filing this
                  under the same condition would leave students and parents with
                  no way to reach their own second factor. Hidden while
                  impersonating and during a support session for the reason the
                  banner exists: the account on screen is not the person at the
                  keyboard, and this is the one screen where acting as someone
                  else means changing how they get back in. */}
              {!isImpersonating && !isSupportSession && (
                <Menu.Item>
                  {({ focus }) => (
                    <Link
                      to={routes.accountSecurity()}
                      className={cn(
                        focus ? "bg-muted" : "",
                        "flex h-auto w-full items-center justify-start gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal text-neutral-700"
                      )}
                    >
                      <ShieldCheckIcon className="size-4" />
                      Sign-in & security
                    </Link>
                  )}
                </Menu.Item>
              )}
              {/* Everyone, and while impersonating too: unlike the item
                  above, it changes nothing about the account. Opened in
                  someone else's shoes it shows their feed and records no
                  visit (see `useWhatsNew`). */}
              <Menu.Item>
                {({ focus }) => (
                  <Link
                    to={routes.whatsNew()}
                    className={cn(
                      focus ? "bg-muted" : "",
                      "flex h-auto w-full items-center justify-start gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal text-neutral-700"
                    )}
                  >
                    <SparklesIcon className="size-4" />
                    What's new
                    {hasUnread && (
                      <span aria-hidden="true" className="ml-auto size-2 rounded-full bg-primary-600" />
                    )}
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {/* `focus`, not the deprecated `active`, is Headless UI v2's
                    name for "this item is the keyboard/pointer target". */}
                {({ focus }) => (
                  <Button
                    variant="ghost"
                    onClick={handleLogout}
                    className={cn(
                      focus ? "bg-muted" : "",
                      "h-auto w-full justify-start gap-2 rounded-[10px] px-3 py-2 text-left text-sm font-normal text-neutral-700"
                    )}
                  >
                    <ArrowRightOnRectangleIcon className="size-4" />
                    Sign out
                  </Button>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </header>
  );
}

/** `AA` for Ada Admin; falls back to a neutral mark for an unnamed session. */
function initials(firstName?: string, lastName?: string): string {
  const first = firstName?.trim()?.[0] ?? "";
  const last = lastName?.trim()?.[0] ?? "";
  return `${first}${last}` || "··";
}
